import { useCallback, useEffect } from 'react';

declare global {
    interface Window {
        zE?: any;
        zEmbed?: any;
        zESettings?: any;
    }
}

const DEFAULT_PLATFORM_NAME = 'DataHub';

interface UseZendeskChatProps {
    userName?: string;
    userEmail?: string;
    companyName?: string;
}

const cleanTag = (value?: string) => value?.replace(/,/g, '').trim();

const buildTags = (companyName?: string, userName?: string) => {
    return [DEFAULT_PLATFORM_NAME, cleanTag(companyName), cleanTag(userName)].filter(Boolean);
};

export const useZendeskChat = ({ userName, userEmail, companyName }: UseZendeskChatProps) => {
    const applyUserContext = useCallback(() => {
        if (typeof window === 'undefined' || typeof window.zE !== 'function') {
            return;
        }

        window.zE(function () {
            if (userName && userEmail) {
                window.zE.identify({
                    name: userName,
                    email: userEmail
                });
            }

            window.zE('webWidget', 'chat:addTags', buildTags(companyName, userName));
        });
    }, [companyName, userEmail, userName]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        window.zESettings = {
            webWidget: {
                color: {
                    theme: '#131c45',
                    launcherText: '#FFFFFF'
                },
                chat: {
                    connectOnPageLoad: false
                },
                contactForm: {
                    suppress: false
                },
                answerBot: {
                    search: {
                        labels: ['datahub']
                    }
                }
            }
        };
    }, []);

    useEffect(() => {
        let retryCount = 0;
        let timeoutId: number | undefined;

        const configureZendesk = () => {
            if (typeof window.zE === 'function') {
                try {
                    window.zE(function () {
                        window.zE.setLocale('en-US');
                        window.zE('webWidget', 'hide');
                        window.zE('webWidget:on', 'close', function () {
                            window.zE('webWidget', 'hide');
                        });
                        window.zE('webWidget:on', 'chat:start', function () {
                            window.zE('webWidget', 'chat:addTags', buildTags(companyName, userName));
                        });
                    });
                    applyUserContext();
                } catch (error) {
                    console.error('Error configuring Zendesk:', error);
                }
                return;
            }

            retryCount += 1;
            if (retryCount <= 20) {
                timeoutId = window.setTimeout(configureZendesk, 500);
            }
        };

        configureZendesk();

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [applyUserContext, companyName, userName]);

    const openChat = useCallback(() => {
        if (typeof window === 'undefined' || typeof window.zE !== 'function') {
            console.warn('Zendesk is not available yet');
            return;
        }

        window.zE(function () {
            window.zE('webWidget', 'chat:addTags', buildTags(companyName, userName));
            window.zE('webWidget', 'show');
            window.zE('webWidget', 'open');
        });
    }, [companyName, userName]);

    return { openChat };
};
